import React from 'react';
import PropTypes from 'prop-types';
import ReactPaginate from 'react-paginate';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import styles from './Pagination.module.scss';

const Pagination = ({
  page,
  pageCount,
  onPageChange,
  total,
  itemLabel,
  showInfo,
  className,
  showJump,
  pageSize,
  pageSizeOptions,
  onPageSizeChange,
}) => {
  const safePageCount = Math.max(0, Number(pageCount) || 0);
  const safePage = Math.min(Math.max(1, Number(page) || 1), Math.max(1, safePageCount));

  if (safePageCount <= 0) return null;

  const wrapperClasses = [styles.wrapper, className || ''].filter(Boolean).join(' ');

  // "Go to page": clamped to the real range, and a no-op when the box
  // still holds the current page (blur after an untouched focus).
  const jump = (raw) => {
    const n = Math.min(Math.max(1, Math.round(Number(raw)) || safePage), safePageCount);
    if (n !== safePage) onPageChange?.(n);
  };

  const hasJump = showJump && safePageCount > 1;
  const hasSize = typeof onPageSizeChange === 'function' && pageSizeOptions.length > 0;

  return (
    <nav className={wrapperClasses} aria-label="Pagination">
      {showInfo && (
        <div className={styles.info}>
          {total != null ? (
            <>
              Page <strong>{safePage}</strong> of <strong>{safePageCount}</strong> ·{' '}
              <strong>{total}</strong> {itemLabel}
            </>
          ) : (
            <>
              Page <strong>{safePage}</strong> of <strong>{safePageCount}</strong>
            </>
          )}
        </div>
      )}
      {(hasJump || hasSize) && (
        <div className={styles.controls}>
          {hasJump && (
            <label className={styles.control}>
              <span>Go to</span>
              <input
                // Re-mounted on each page change so the box follows the
                // pager without being a controlled input.
                key={safePage}
                className={styles.jump}
                type="number"
                inputMode="numeric"
                min={1}
                max={safePageCount}
                defaultValue={safePage}
                aria-label="Page number"
                onKeyDown={(event) => {
                  if (event.key === 'Enter') {
                    event.preventDefault();
                    jump(event.currentTarget.value);
                  }
                }}
                onBlur={(event) => jump(event.currentTarget.value)}
              />
            </label>
          )}
          {hasSize && (
            <label className={styles.control}>
              <span>Per page</span>
              <select
                className={styles.size}
                value={pageSize ?? pageSizeOptions[0]}
                aria-label={`${itemLabel} per page`}
                onChange={(event) => onPageSizeChange(Number(event.target.value))}
              >
                {pageSizeOptions.map((size) => (
                  <option key={size} value={size}>
                    {size}
                  </option>
                ))}
              </select>
            </label>
          )}
        </div>
      )}
      <ReactPaginate
        forcePage={safePage - 1}
        pageCount={safePageCount}
        onPageChange={({ selected }) => onPageChange?.(selected + 1)}
        marginPagesDisplayed={1}
        pageRangeDisplayed={3}
        previousLabel={<ChevronLeft size={16} />}
        nextLabel={<ChevronRight size={16} />}
        breakLabel="…"
        containerClassName={styles.list}
        previousClassName={styles.edge}
        nextClassName={styles.edge}
        breakClassName="break"
        activeClassName="selected"
        disabledClassName="disabled"
        renderOnZeroPageCount={null}
      />
    </nav>
  );
};

Pagination.propTypes = {
  page: PropTypes.number.isRequired,
  pageCount: PropTypes.number.isRequired,
  onPageChange: PropTypes.func.isRequired,
  total: PropTypes.number,
  itemLabel: PropTypes.string,
  showInfo: PropTypes.bool,
  className: PropTypes.string,
  /** Show a "Go to" page-number box (only when there is more than one page). */
  showJump: PropTypes.bool,
  /** Current page size; with onPageSizeChange, shows a "Per page" select. */
  pageSize: PropTypes.number,
  pageSizeOptions: PropTypes.arrayOf(PropTypes.number),
  onPageSizeChange: PropTypes.func,
};

Pagination.defaultProps = {
  total: null,
  itemLabel: 'items',
  showInfo: true,
  className: '',
  showJump: false,
  pageSize: null,
  pageSizeOptions: [50, 100, 200],
  onPageSizeChange: null,
};

export default Pagination;
